/**
 * Converts a date string (YYYY-MM-DD) to the start of that day in Copenhagen time,
 * expressed as a UTC ISO string.
 */
export function dateToCopenhagenStartOfDayUTC(dateString: string): string {
  const [year, month, day] = dateString.split("-").map(Number);

  // Create a date at noon UTC on this date (to avoid boundary issues)
  const refDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  // Get the hour in Copenhagen for this reference point
  const copHourStr = refDate.toLocaleString("en-US", {
    timeZone: "Europe/Copenhagen",
    hour: "numeric",
    hour12: false,
  });
  const copHour = parseInt(copHourStr);

  // Calculate Copenhagen's offset from UTC (should be 1 or 2)
  const offset = copHour - 12;

  // Midnight Copenhagen = midnight UTC - offset hours
  const midnightCopenhagenUTC = new Date(
    Date.UTC(year, month - 1, day, -offset, 0, 0)
  );

  return midnightCopenhagenUTC.toISOString();
}

/**
 * Converts a date string (YYYY-MM-DD) to the end of that day in Copenhagen time
 * (= start of the next day), expressed as a UTC ISO string.
 * Use this for "to" / end-date filters to make the range inclusive of the full day.
 */
export function dateToCopenhagenEndOfDayUTC(dateString: string): string {
  const [year, month, day] = dateString.split("-").map(Number);
  const nextDay = new Date(year, month - 1, day + 1);
  const nextDayStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, "0")}-${String(nextDay.getDate()).padStart(2, "0")}`;
  return dateToCopenhagenStartOfDayUTC(nextDayStr);
}

/**
 * Renders a UTC ISO timestamp as Copenhagen local time (DD.MM.YYYY, HH:mm).
 * Returns "-" for empty input and echoes the raw string if it cannot be parsed.
 */
export function formatCopenhagenDateTime(
  dateString: string | null | undefined
): string {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleString("da-DK", {
      timeZone: "Europe/Copenhagen",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}
